import React from 'react';
import { Col, Row, Skeleton } from 'antd';
import { I18n } from '@aws-amplify/core';

import { Refetch } from '@/components/ui/Refetch';
import { useBeraReportV2 } from '@/context/BeraReportV2';
import { useGetStrainIndex } from '@/hooks/useGetStrainIndex';
import { useGetBeraSummaryResultV2 } from '@/hooks/useGetBeraSummaryResultV2';
import type { BeraReportV2 } from '@/types/Bera';
import { toFirstStretchFrameUrl } from '@/components/views/BeraJob/BeraReportV2/utils';
import { useReportV2Result } from '@/components/views/BeraJob/BeraReportV2/_components/ReportResult/_hooks/useReportV2Result';
import {
	ResultBody,
	ResultComment,
	ResultDivider,
	ResultHeader,
	ResultInformations,
	VideoFrame
} from '@/components/views/BeraJob/BeraReportV2/_components/ReportResult';

import { Actions } from './Actions';
import { Footer } from './Footer';
import { Content } from './styles';
import { useResultActions } from './_hooks';

interface ResultProps {
	report: BeraReportV2;
	onPrevious: () => void;
	onClose: () => void;
}

export function Result({ report, onPrevious, onClose }: Readonly<ResultProps>): JSX.Element {
	const { file, jobSummary, organizationId, companyId, videoUrl } = useBeraReportV2();

	const tenant = { organization_id: organizationId, company_id: companyId };

	const { result, isLoading, isError, refetch } = useReportV2Result({ report, file, jobSummary });

	const strainIndex = useGetStrainIndex({ ...tenant, file_id: file.id });

	const actionParams = { ...tenant, report_id: report.id, result };

	const {
		comment,
		isConsolidated,
		isUpdating,
		isDownloading,
		handleCommentChange,
		handleCommentBlur,
		handleToggleConsolidate,
		handleDownloadPdf
	} = useResultActions(actionParams);

	const summaryResult = useGetBeraSummaryResultV2({ ...tenant, id: jobSummary.id });

	const isAnalysisConsolidated = !!summaryResult.data?.consolidated;

	const isLoadingAnalysis = summaryResult.isInitialLoading;

	const isPreviousDisabled = isConsolidated || isAnalysisConsolidated || isLoadingAnalysis;

	const visibleStrainIndex = strainIndex.isError ? undefined : strainIndex.data;

	const hasVideo = !!videoUrl;

	if (isLoading) {
		return <Skeleton active />;
	}

	if (isError || !result) {
		return <Refetch onClick={refetch} />;
	}

	return (
		<Row gutter={[0, 32]}>
			<Col span={24}>
				<Content>
					<Row gutter={[0, 40]}>
						<Col span={24}>
							<ResultHeader
								subtitle={I18n.get('Results')}
								title={I18n.get('Basic Ergonomics Risk Assessment (B.E.R.A.)')}
							/>
						</Col>
						<Col span={24}>
							<ResultInformations informations={result.informations} scores={result.scores} />
						</Col>
						{hasVideo && (
							<Col span={24}>
								<VideoFrame src={toFirstStretchFrameUrl(videoUrl, report)} />
							</Col>
						)}
						<Col span={24}>
							<ResultDivider />
						</Col>
						<ResultBody steps={result.steps} strainIndex={visibleStrainIndex} />
						<Col span={24}>
							<ResultComment
								value={comment}
								disabled={isConsolidated}
								onChange={handleCommentChange}
								onBlur={handleCommentBlur}
							/>
						</Col>
						<Col span={24}>
							<Actions
								consolidated={isConsolidated}
								downloading={isDownloading}
								consolidating={isUpdating}
								onDownload={handleDownloadPdf}
								onToggleConsolidate={handleToggleConsolidate}
							/>
						</Col>
					</Row>
				</Content>
			</Col>
			<Col span={24}>
				<Footer previousDisabled={isPreviousDisabled} onPrevious={onPrevious} onClose={onClose} />
			</Col>
		</Row>
	);
}
